import React from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiTrendingUp, FiTarget, FiAlertCircle, FiCheckCircle, FiArrowDown } = FiIcons;

function ReportSummary({ overallScore, overallPerformance, areaScores }) {
  const topPerformingAreas = [...areaScores]
    .sort((a, b) => b.score - a.score)
    .slice(0, 3);

  const improvementAreas = [...areaScores]
    .sort((a, b) => a.score - b.score)
    .slice(0, 3);

  const maxTotalScore = 280;
  const overallPercentage = (overallScore / maxTotalScore) * 100;

  const scrollToNextSteps = () => {
    const element = document.getElementById('action-plan');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <motion.div 
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.2 }}
      className="bg-white rounded-2xl shadow-xl p-8 mb-8"
    >
      <h2 className="text-3xl font-bold text-gray-800 mb-8 text-center uppercase tracking-wide">
        Executive Summary
      </h2>

      {/* Overall Score Section */}
      <div className="text-center mb-12">
        <div className="relative inline-flex items-center justify-center w-48 h-48">
          {/* Circular Background Trace */}
          <svg className="absolute w-full h-full transform -rotate-90">
            <circle
              cx="96" cy="96" r="80"
              stroke="currentColor"
              strokeWidth="12"
              fill="transparent"
              className="text-gray-100"
            />
            <motion.circle
              cx="96" cy="96" r="80"
              stroke="currentColor"
              strokeWidth="12"
              fill="transparent"
              strokeDasharray={502.6}
              initial={{ strokeDashoffset: 502.6 }}
              animate={{ strokeDashoffset: 502.6 - (502.6 * overallPercentage) / 100 }}
              transition={{ duration: 1.5, ease: "easeOut" }}
              className="text-blue-600"
            />
          </svg>
          <div className="flex flex-col items-center relative z-10">
            <div className="text-5xl font-black text-gray-800">{overallScore}</div>
            <div className="text-xs text-gray-400 font-bold tracking-widest uppercase">/ {maxTotalScore}</div>
          </div>
        </div>
        
        <div className="mt-8">
          <div className={`inline-flex items-center px-8 py-3 rounded-full text-xl font-bold shadow-md ${overallPerformance.bgColor} ${overallPerformance.color}`}>
            <SafeIcon icon={overallPercentage >= 70 ? FiCheckCircle : FiAlertCircle} className="w-6 h-6 mr-3" />
            {overallPerformance.level} Status
          </div>
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-8 mb-10">
        {/* Top Performing Areas */}
        <div className="bg-green-50/50 rounded-2xl p-6 border border-green-100">
          <div className="flex items-center mb-6">
            <div className="w-10 h-10 rounded-xl bg-green-100 flex items-center justify-center mr-4">
              <SafeIcon icon={FiTrendingUp} className="w-6 h-6 text-green-600" />
            </div>
            <h3 className="text-xl font-bold text-green-900">Key Strengths</h3>
          </div>
          <div className="space-y-3">
            {topPerformingAreas.map((area) => (
              <div key={area.id} className="flex items-center justify-between p-4 bg-white rounded-xl shadow-sm border border-green-50">
                <span className="flex items-center text-gray-700 font-semibold text-lg">
                  <span className="mr-3 text-2xl">{area.icon}</span> {area.title}
                </span>
                <span className="font-bold text-green-600">{area.score}/35</span>
              </div>
            ))}
          </div>
        </div>

        {/* Improvement Opportunities */}
        <div className="bg-amber-50/50 rounded-2xl p-6 border border-amber-100">
          <div className="flex items-center mb-6">
            <div className="w-10 h-10 rounded-xl bg-amber-100 flex items-center justify-center mr-4">
              <SafeIcon icon={FiTarget} className="w-6 h-6 text-amber-600" />
            </div>
            <h3 className="text-xl font-bold text-amber-900">Growth Opportunities</h3>
          </div>
          <div className="space-y-3">
            {improvementAreas.map((area) => (
              <div key={area.id} className="flex items-center justify-between p-4 bg-white rounded-xl shadow-sm border border-amber-50">
                <span className="flex items-center text-gray-700 font-semibold text-lg">
                  <span className="mr-3 text-2xl">{area.icon}</span> {area.title}
                </span>
                <span className="font-bold text-amber-600">{area.score}/35</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Strategic Vision */}
      <div className="p-8 bg-slate-900 rounded-2xl text-white shadow-2xl relative overflow-hidden group">
        <div className="absolute top-0 right-0 p-8 opacity-5 group-hover:opacity-10 transition-opacity">
          <SafeIcon icon={FiArrowDown} className="w-32 h-32" />
        </div>
        
        <h3 className="text-2xl font-bold mb-6 flex items-center">
          <span className="bg-blue-600 w-2 h-8 rounded-full mr-4 inline-block"></span>
          Strategic Outlook
        </h3>
        
        <div className="grid md:grid-cols-2 gap-10">
          <div>
            <h4 className="text-blue-400 font-bold uppercase text-xs tracking-widest mb-3">Assessment Context</h4>
            <p className="text-gray-300 leading-relaxed text-lg font-light">
              {overallPercentage >= 85 && "Your organization displays elite-level maturity. Your challenge is not foundational, but rather the pursuit of incremental optimization and sustaining this high-performance culture."}
              {overallPercentage >= 70 && overallPercentage < 85 && "You have a robust foundation with clear operational strength. There are specific strategic levers identified below that will move you into the upper quartile of performance."}
              {overallPercentage >= 55 && overallPercentage < 70 && "Your organization is in a 'Developing' phase. While core activities are functional, lack of consistency in strategy or leadership alignment is limiting your growth potential."}
              {overallPercentage < 55 && "The assessment indicates significant structural opportunities. Focus on stabilizing leadership alignment and core operational processes first before attempting rapid scaling."}
            </p>
          </div>
          <div>
            <h4 className="text-blue-400 font-bold uppercase text-xs tracking-widest mb-3">Primary Recommendation</h4>
            <p className="text-gray-300 leading-relaxed text-lg font-light">
              {overallPercentage >= 70 
                ? "Leverage your high-scoring areas to mentor and improve the lagging segments. We recommend a strategic audit to identify 'blind spots' in your highest-performing teams."
                : "A focused intervention in your three lowest-scoring areas will yield the highest ROI for organizational health. Prioritize process mapping and leadership communication sessions."
              }
            </p>
            <button 
              onClick={scrollToNextSteps}
              className="mt-8 px-6 py-3 bg-blue-600 hover:bg-blue-500 rounded-xl font-bold transition-all flex items-center gap-2 shadow-lg hover:shadow-blue-500/20"
            >
              View Full Implementation Plan <SafeIcon icon={FiArrowDown} />
            </button>
          </div>
        </div>
      </div>
    </motion.div>
  );
}

export default ReportSummary;